import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Coffee, ArrowRight, Gift } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const RoastSelection: React.FC = () => {
  const navigate = useNavigate();

  const roasts = [
    {
      id: 'hammer-fist',
      name: 'Hammer Fist',
      emoji: '🤜',
      batch: 'New Release',
      tagline: "A blend for friends who've seen everything",
      description: "A blend of Guatemala, Ethiopia, and Tanzania. Three very different origins, each bringing something of their own, but better when they come together. Named for a self-defense move that never stopped being funny.",
      image: '/ChatGPT_Image_Aug_18,_2026,_09_43_45_AM.png',
      roastLevel: 'Medium Blend',
      tastingNotes: ['Rich', 'Fruity', 'Complex', 'Friendship'],
      isGiftBox: false,
      linkToPage: '/hammer-fist',
      isSoldOut: false
    },
    {
      id: 'festival-blend',
      name: 'Festival Blend',
      emoji: '🎨',
      batch: 'Limited Edition',
      tagline: 'Art. Community. 60 years of showing up.',
      description: 'Created in collaboration with the Marblehead Festival of Arts. A smooth, balanced medium roast celebrating creativity, community, and the quiet power of people gathering together. $25 from every bag goes directly to the Festival of Arts.',
      image: '/ChatGPT_Image_May_20,_2026_at_10_36_49_AM.png',
      roastLevel: 'Smooth Medium',
      tastingNotes: ['Warm', 'Balanced', 'Smooth', 'Community'],
      isGiftBox: false,
      linkToPage: '/festival-blend',
      isSoldOut: false
    },
    {
      id: 'regiment-roast',
      name: 'Regiment Roast',
      emoji: '⚓',
      batch: 'New Release',
      tagline: 'Community. Craft. Showing up.',
      description: 'A full-bodied medium roast meant for early mornings, shared tables, and the quiet moments before the day gets moving. A tribute to Glover\'s Regiment and the spirit of Marblehead.',
      image: '/9e11bedb-5c1b-4989-b3be-9d545225578d.png',
      roastLevel: 'Full-Bodied Medium',
      tastingNotes: ['Full-Bodied', 'Community', 'Heritage', 'Craft'],
      isGiftBox: false,
      linkToPage: '/regiment-roast',
      isSoldOut: true,
      soldOutMessage: "We're busy roasting — back soon!"
    },
    {
      id: 'wake-with-drake',
      name: 'Wake with Drake',
      emoji: '🏈',
      batch: 'New Release',
      tagline: 'Because football mornings just hit different',
      description: 'For football mornings, late games, early kickoffs, and the small rituals that stick around. Bold, smooth, and dependable — the kind of cup you want before kickoff, school drop-off, or talking trash with your kids.',
      image: '/chatgpt_image_jan_12,_2026_at_02_10_15_pm.png',
      roastLevel: 'Medium',
      tastingNotes: ['Bold', 'Smooth', 'Dependable', 'Game Day'],
      isGiftBox: false,
      linkToPage: '/wake-with-drake',
      isSoldOut: true
    },
    {
      id: 'header-holiday',
      name: 'Header Holiday',
      emoji: '🎁',
      batch: 'Limited Edition',
      tagline: 'A partnership with SPUR to support families in our community',
      description: 'A partnership with SPUR to support families in our community. Complete gift box with 12oz coffee, commemorative bamboo spoon, and story card. Your purchase helps provide essential resources and joy to those who need it most.',
      image: '/Header Holiday Bag Label (1) copy.png',
      roastLevel: 'Medium',
      tastingNotes: ['Chocolate', 'Caramel', 'Smooth', 'Festive'],
      isGiftBox: true,
      linkToPage: '/header-holiday',
      isSoldOut: true
    },
    {
      id: 'barnegat-breakfast',
      name: 'Barnegat Breakfast',
      emoji: '🌅',
      batch: 'Batch 1',
      tagline: 'Our signature morning blend',
      description: 'Named after the place where Marblehead\'s story began. A perfect balance of chocolate, nuts, honey, and citrus.',
      image: 'https://i.ibb.co/Kj3zbtXd/Barnegat-Breakfast-4-x-6-in.png',
      roastLevel: 'Medium',
      tastingNotes: ['Chocolate', 'Nutty', 'Honey', 'Citrus']
    },
    {
      id: 'soleless-shakedown',
      name: 'Soleless Shakedown',
      emoji: '⚡',
      batch: 'Batch 1',
      tagline: 'Inspired by Hocus Pocus',
      description: 'A tribute to the iconic Hocus Pocus scene on Old Burial Hill. Bold, smooth, and just a little mischievous.',
      image: 'https://i.ibb.co/yB83W3DB/Soleless-Shakedown-Final.png',
      roastLevel: 'Medium-Dark',
      tastingNotes: ['Cocoa', 'Roasted Nuts', 'Whisper of Smoke', 'Hints of Stolen Sneakers*'],
      isSoldOut: true
    },
    {
      id: 'nunnepog-fog',
      name: 'Nunnepog Fog Espresso Blend',
      emoji: '🌫️',
      batch: 'Batch 1',
      tagline: 'The world can wait. Pour the quiet.',
      description: 'Designed for espresso lovers, perfect for any brew. Crafted for depth and balance, it shines in any form — pour-over, drip, or French press.',
      image: 'https://i.ibb.co/xtC0KDLt/Nunnepog-Fog-4-x-6-in-1.png',
      roastLevel: 'Espresso Roast',
      tastingNotes: ['Misty', 'Honey', 'Hazelnut', 'Gentle']
    }
  ];

  return (
    <div className="min-h-screen bg-[#132947]">
      <Header />

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="text-6xl mb-6">☕</div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Rate Your Roast
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Which Bond Coffee roast would you like to rate? Your feedback helps us perfect our craft.
            </p>
          </div>

          {/* Roast Selection Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {roasts.map((roast) => (
              <div
                key={roast.id}
                className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl border border-white/20 overflow-hidden hover:border-[#fda36b]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#fda36b]/10 group"
              >
                {/* Roast Image */}
                <div className="aspect-[3/4] overflow-hidden bg-white/5 p-6">
                  <img
                    src={roast.image}
                    alt={roast.name}
                    className="w-full h-full object-cover rounded-lg group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                {/* Roast Details */}
                <div className="p-6 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-3xl">{roast.emoji}</span>
                      <span className="text-xs bg-[#fda36b]/20 text-[#fda36b] px-2 py-1 rounded-full font-semibold">
                        {roast.batch}
                      </span>
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">
                      {roast.name}
                    </h2>
                    <p className="text-[#fda36b] text-sm font-semibold mb-3">
                      {roast.tagline}
                    </p>
                    <p className="text-white/70 text-sm leading-relaxed mb-4">
                      {roast.description}
                    </p>
                  </div>

                  {/* Roast Level */}
                  <div className="flex items-center justify-between text-sm pb-4 border-b border-white/10">
                    <span className="text-white/60">Roast Level:</span>
                    <span className="text-white font-semibold">{roast.roastLevel}</span>
                  </div>

                  {/* Tasting Notes */}
                  <div>
                    <div className="text-white/60 text-xs font-semibold mb-2">TASTING NOTES</div>
                    <div className="flex flex-wrap gap-2">
                      {roast.tastingNotes.map((note, index) => (
                        <span
                          key={index}
                          className="text-xs bg-white/10 text-white/90 px-2 py-1 rounded"
                        >
                          {note}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {roast.isGiftBox ? (
                    <div className="space-y-2">
                      {roast.isSoldOut ? (
                        <button
                          disabled
                          className="w-full bg-white/10 text-white/50 px-6 py-3 rounded-lg font-semibold cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <Gift className="w-5 h-5" />
                          {roast.soldOutMessage ?? 'Sold Out'}
                        </button>
                      ) : (
                        <button
                          onClick={() => navigate(roast.linkToPage)}
                          className="w-full bg-[#fda36b] text-[#132947] px-6 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center justify-center gap-2 group-hover:shadow-lg"
                        >
                          <Gift className="w-5 h-5" />
                          Order Now
                        </button>
                      )}
                      <button
                        onClick={() => navigate(roast.linkToPage)}
                        className="w-full bg-white/10 text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                      >
                        Learn More
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {roast.isSoldOut ? (
                        <button
                          disabled
                          className="w-full bg-white/10 text-white/50 px-6 py-3 rounded-lg font-semibold cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          <Coffee className="w-5 h-5" />
                          {roast.soldOutMessage ?? 'Sold Out'}
                        </button>
                      ) : roast.linkToPage ? (
                        <>
                          <button
                            onClick={() => navigate(roast.linkToPage)}
                            className="w-full bg-[#fda36b] text-[#132947] px-6 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center justify-center gap-2 group-hover:shadow-lg"
                          >
                            <Coffee className="w-5 h-5" />
                            Order Now
                          </button>
                          <button
                            onClick={() => navigate(roast.linkToPage)}
                            className="w-full bg-white/10 text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300 flex items-center justify-center gap-2 text-sm"
                          >
                            Learn More
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => navigate(`/rateyouroast/${roast.id}`)}
                          className="w-full bg-[#fda36b] text-[#132947] px-6 py-3 rounded-lg font-semibold hover:bg-[#fda36b]/90 transition-all duration-300 flex items-center justify-center gap-2 group-hover:shadow-lg"
                        >
                          Rate This Roast
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Info Section */}
          <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-8 rounded-2xl border border-[#fda36b]/20 text-center">
            <Coffee className="w-12 h-12 text-[#fda36b] mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-4">
              Your Feedback Matters
            </h3>
            <p className="text-white/80 max-w-2xl mx-auto">
              We read every single review and use your feedback to perfect our roasting process.
              Whether it's your first cup or your hundredth, your insights help us create better coffee for everyone.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RoastSelection;
